// c60_46_phase_2_auth_gating — global auth token attach.
//
// Phase 2 of the security audit gates a broad set of previously-open
// backend routers behind `Depends(get_current_user)`. The existing
// frontend attaches the JWT per-call and MANY call sites (axios AND
// raw fetch) sent no token. Rather than edit every call site, this
// module installs two shims once at app startup so EVERY request to
// our own backend carries `Authorization: Bearer <token>` when a
// token exists in localStorage:
//   1. an axios request interceptor, and
//   2. a window.fetch wrapper.
//
// Scope guard: the token is only attached to requests targeting our
// backend (REACT_APP_BACKEND_URL or a relative `/api...` path) so we
// never leak the JWT to third-party hosts. Any Authorization header
// the caller already set is preserved.
import axios from "axios";
import { getActiveCompany } from "./company";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "snagger_token";
// c61_867 — while another company's Books page (FTMD) is on screen, every backend call says so
export const COMPANY_HEADER = "X-Snagger-Company";

function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
}

function targetsBackend(url) {
  if (!url) return false;
  let u = url;
  if (typeof u !== "string") {
    try {
      u = String(u);
    } catch (e) {
      return false;
    }
  }
  if (u.startsWith("/")) return u.startsWith("/api");
  if (BACKEND && u.startsWith(BACKEND)) return true;
  return false;
}

// 1b) c61_711 — the backend answers upstream failures (Veeqo / Walmart /
// QBO) with 424 + X-Original-Status instead of 502/504, because Cloudflare
// swallows origin 502/504 bodies and shows its own page. Map the status
// back here so every existing `status === 502 / 504` branch keeps working
// and a REAL 502 (Cloudflare page, no header) stays a gateway error.
// 1c) c61_784 — a 401 "Token expired" / "Session expired (new deployment)" /
// "Invalid token" from OUR backend means the saved login is dead (72 h
// JWT, or a redeploy). Before, a tab left open just showed that text as
// the error of whatever was clicked (e.g. "Connect Zoho WorkDrive"). Now
// AuthContext hears this event, signs out and shows the login screen.
export const SESSION_EXPIRED_EVENT = "snagger:session-expired";
const DEAD_SESSION = /^(token expired|session expired|invalid token|missing auth token)/i;

export function announceDeadSession(detail) {
  try {
    if (typeof detail === "string" && DEAD_SESSION.test(detail) && getToken()) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
    }
  } catch (e) {
    // never throw from an interceptor
  }
}

axios.interceptors.response.use(undefined, (error) => {
  try {
    const r = error && error.response;
    const orig = r && r.status === 424 && Number(r.headers && r.headers["x-original-status"]);
    if (orig) {
      r.status = orig;
      r.statusText = orig === 504 ? "Gateway Timeout" : "Bad Gateway";
    }
    const url = (error && error.config && `${error.config.baseURL || ""}${error.config.url || ""}`) || "";
    if (r && r.status === 401 && targetsBackend(url) && !/\/api\/auth\/login/.test(url)) {
      announceDeadSession(r.data && r.data.detail);
    }
  } catch (e) {
    // never mask the original error
  }
  return Promise.reject(error);
});

// 1) axios request interceptor
axios.interceptors.request.use((config) => {
  try {
    const full = `${config.baseURL || ""}${config.url || ""}`;
    if (targetsBackend(config.url) || targetsBackend(full)) {
      const token = getToken();
      const headers = config.headers || {};
      const hasAuth = headers.Authorization || headers.authorization;
      if (token && !hasAuth) {
        config.headers = { ...headers, Authorization: `Bearer ${token}` };
      }
      const company = getActiveCompany();
      if (company && !(config.headers || {})[COMPANY_HEADER]) {
        config.headers = { ...(config.headers || {}), [COMPANY_HEADER]: company };
      }
    }
  } catch (e) {
    // never block a request on the interceptor
  }
  return config;
});

// 2) window.fetch wrapper
if (typeof window !== "undefined" && typeof window.fetch === "function") {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    try {
      const url =
        typeof input === "string"
          ? input
          : (input && input.url) || "";
      if (targetsBackend(url)) {
        const token = getToken();
        const company = getActiveCompany();
        if (token || company) {
          const headers = new Headers(
            (init && init.headers) ||
              (typeof input !== "string" && input && input.headers) ||
              {},
          );
          let changed = false;
          if (token && !headers.has("Authorization")) {
            headers.set("Authorization", `Bearer ${token}`);
            changed = true;
          }
          if (company && !headers.has(COMPANY_HEADER)) {
            headers.set(COMPANY_HEADER, company);
            changed = true;
          }
          if (changed) init = { ...init, headers };
        }
      }
    } catch (e) {
      // fall through to the untouched fetch on any error
    }
    const p = originalFetch(input, init);
    try {
      const url = typeof input === "string" ? input : (input && input.url) || "";
      if (targetsBackend(url) && !/\/api\/auth\/login/.test(url)) {
        p.then((res) => {
          if (res && res.status === 401) {
            res.clone().json().then((b) => announceDeadSession(b && b.detail)).catch(() => {});
          }
        }).catch(() => {});
      }
    } catch (e) {
      // never interfere with the caller's promise
    }
    return p;
  };
}
